import { useState, useCallback, useEffect } from 'react';
import { useField } from 'formik';
import { AISMessageTypes } from 'store/declarations/enums/common/declaration-types';
import { getTemplates } from 'store/template/client';
import { TemplateResponse } from 'store/template/template';
import FormSelect, { SelectOption } from 'components/new-ui/composed/formSelect/FormSelect';
import TemplateIconOutlined from 'components/icons/TemplateIconOutlined';
import ReadonlyFormItem from 'components/new-ui/base/form/ReadonlyFormItem';
import { StyledFormSectionContentWrapper } from '../../../styles';
import { NewShipmentFormFields, Steps } from '../../../types';
import { useMultiStepFormContext } from 'components/new-ui/composed/multistep-form/MultiStepFormProvider';
import FormSectionHeader from 'components/new-ui/base/form/FormSectionHeader';

const Template = () => {
    const { isComplete, isActive, isIdle, currentStepConfig, resetStep } =
        useMultiStepFormContext<NewShipmentFormFields>();
    const [field, meta, helpers] = useField('template.templateId');
    const placeholder = 'Select template';
    const stepKey = Steps.TEMPLATE;
    const disabled = currentStepConfig?.disabled ?? false;

    const [templates, setTemplates] = useState<TemplateResponse[]>([]);

    const handleReset = () => {
        resetStep(stepKey, 'templateId');
    };

    const getIrelandH7Templates = useCallback(async () => {
        try {
            const response = await getTemplates('ireland', 'import', AISMessageTypes.H7, {
                size: 9999,
            });

            if (!response?.list) {
                throw new Error('');
            }

            return response.list;
        } catch (error) {
            console.log(error);
            return [];
        }
    }, []);

    useEffect(() => {
        getIrelandH7Templates().then((templates) => setTemplates(templates));
    }, [getIrelandH7Templates]);

    const selectedTemplate = templates.find((t) => t.id === field.value);

    const options: SelectOption[] = templates.map((t) => ({
        id: t.id,
        value: t?.templateName ?? '-',
    }));

    if (isIdle(Steps.TEMPLATE)) {
        return <FormSectionHeader title="Template" icon={<TemplateIconOutlined />} />;
    }

    if (isComplete(Steps.TEMPLATE)) {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                }}
            >
                <FormSectionHeader title="Template" icon={<TemplateIconOutlined />} />
                <ReadonlyFormItem
                    disabled={disabled}
                    onReset={handleReset}
                    value={selectedTemplate?.templateName ?? ''}
                />
            </div>
        );
    }

    if (isActive(Steps.TEMPLATE)) {
        return (
            <StyledFormSectionContentWrapper>
                <FormSectionHeader title="Template" icon={<TemplateIconOutlined />} />
                <div>
                    <FormSelect
                        placeholder={placeholder}
                        style={{
                            flexGrow: 1,
                            maxWidth: '100%',
                        }}
                        required
                        hideKeys={true}
                        selectOptions={options}
                        fieldHelper={helpers}
                        fieldMeta={meta}
                        fieldProps={field}
                    />
                </div>
            </StyledFormSectionContentWrapper>
        );
    }

    return null;
};

export default Template;
